import React from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'd MMM yyyy');
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'd MMM yyyy, HH:mm');
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">{title}</h3>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const fmtBinList = (item) => {
  const list = item?.batchDetails || [];
  const binCodes = list
    .map((b) => b?.binId && typeof b.binId === 'object' ? (b.binId.binCode ?? b.binId._id) : (b?.binCode ?? b?.binId))
    .filter(Boolean);
  if (!binCodes.length) return '—';
  return [...new Set(binCodes.map(String))].join(', ');
};

const OutwardDetailsModal = ({ open, onClose, loading, error, data, onRetry }) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="outward-details-title"
    >
      <div className="absolute inset-0 bg-black/20" onClick={handleBackdropClick} aria-hidden="true" />
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-[18px] border border-[#EEF1F5] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-2">
          <h2 id="outward-details-title" className="text-lg font-semibold text-[#101828]">
            {loading ? 'Outward Details' : `Outward Details — ${data?.transactionId || data?._id || '—'}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-[#D0D5DD] text-[#667085] flex items-center justify-center bg-white hover:bg-[#F6F7FA] transition"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <hr className="border-0 border-t border-[#EEF1F5] mt-2" />

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div className="py-8 text-center text-gray-500 text-sm">Loading...</div>}

          {error && !loading && (
            <div className="py-8 text-center">
              <p className="text-red-600 text-sm mb-3">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-xl border border-[#188742] bg-[#188742]/10 px-4 py-2 text-sm font-medium text-[#188742] hover:bg-[#188742]/20"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {!loading && !error && data && (
            <>
              <Section title="Basic">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Transaction ID:</span> {data.transactionId ?? '—'}</div>
                  <div><span className="text-gray-500">Invoice Number:</span> {data.invoiceNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Invoice Date:</span> {fmtDate(data.invoiceDate)}</div>
                  <div><span className="text-gray-500">DO Number:</span> {data.deliveryOrderNumber || "—"}</div>
                  <div><span className="text-gray-500">Indent Ref:</span> {data.indentReferenceNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Status:</span> {data.status ?? '—'}</div>
                  <div><span className="text-gray-500">Dispatch Date:</span> {fmtDateTime(data.dispatchDate)}</div>
                </div>
                <div className="mt-2">
                  <span className="text-gray-500">Company: </span>
                  {(data.companies && data.companies.length > 0)
                    ? data.companies.map((c) => `${c.name || '—'} (${c.companyCode ?? '—'})`).join(', ')
                    : '—'}
                </div>
              </Section>

              <Section title="Vehicle / Dock">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Vehicle No:</span> {data.vehicleNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Vehicle Temp:</span> {data.vehicleTemperature ?? '—'}</div>
                  <div><span className="text-gray-500">Product Temperature:</span> {data.productTemperature != null ? data.productTemperature : '—'}</div>
                  <div><span className="text-gray-500">Dock Number:</span> {data.dockNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Dock In Time:</span> {fmtDateTime(data.dockInTime)}</div>
                  <div><span className="text-gray-500">Dock Out Time:</span> {fmtDateTime(data.dockOutTime)}</div>
                  <div><span className="text-gray-500">Reporting Time:</span> {fmtDateTime(data.reportingTime)}</div>
                  <div><span className="text-gray-500">Reporting date:</span> {fmtDateTime(data.reportingDate)}</div>
                </div>
              </Section>

              <Section title="Items">
                <div className="overflow-x-auto rounded-xl border border-[#EEF1F5]">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#F6F7FA]">
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tl-xl">SKU</th>
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Product</th>
                        {/* <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Bin(s)</th> */}
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2 rounded-tr-xl">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.items && data.items.length > 0) ? data.items.map((item, i) => {
                        const p = item.productId;
                        const sku = p && typeof p === 'object' ? (p.sku ?? p.skuName) : p;
                        const name = (p && typeof p === 'object' ? (p.skuName ?? p.description) : null) ?? item.productDescription ?? '—';
                        return (
                          <tr key={i} className="border-t border-[#EEF1F5]">
                            <td className="px-3 py-2 text-[#101828]">{sku ?? '—'}</td>
                            <td className="px-3 py-2 text-[#101828]">{name}</td>
                            {/* <td className="px-3 py-2 text-[#101828]">{fmtBinList(item)}</td> */}
                            <td className="px-3 py-2 text-right text-[#101828]">{item.dispatchQuantity ?? '—'}</td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={4} className="px-3 py-4 text-center text-gray-500">No items</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          )}
        </div>

        {!loading && (
          <div className="shrink-0 px-6 py-4 border-t border-[#EEF1F5] flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default OutwardDetailsModal;

